// Case-wide links and individual Actions are explicit choices. Before anything
// is typed, the most recently updated open Cases are offered with their open
// Actions. A Case's full Action list is read only when the person expands it.
import { useQuery } from "@tanstack/react-query";
import { m } from "@trameria/i18n";
import { useState } from "react";
import { useApi } from "../app/context";
import { dateIn } from "../cases/dates";
import { caseQuery, openCasesQuery } from "../cases/queries";
import { apiErrorMessage } from "../data/errors";
import { type SearchHit, searchQuery } from "../refs/queries";
import { Caret } from "../ui/icon";
import { useLinkBlock } from "./queries";
import { useTimeBlockerScreen } from "./screen-state";

/** How many Cases are offered before anything is typed. */
const RECENT_CASES = 8;

export function LinkPicker({
  blockId,
  planOn,
}: {
  blockId: string;
  planOn?: string;
}) {
  const screen = useTimeBlockerScreen(),
    api = useApi(),
    link = useLinkBlock();
  const [q, setQ] = useState(""),
    [error, setError] = useState<string | null>(null);
  const {
    data,
    isFetching,
    error: searchError,
  } = useQuery(searchQuery(api, q, ["case"]));
  // Nothing typed yet: the open Cases, newest update first.
  const idle = q.trim() === "";
  const recent = useQuery({ ...openCasesQuery(api, null), enabled: idle });
  const choose = (caseId: string, stepId?: string) =>
    link.mutate(
      {
        ...screen.place,
        day:
          planOn ??
          dateIn(new Date(screen.dayStart).toISOString(), screen.timeZone),
        blockId,
        target: { caseId, stepId },
      },
      {
        onError: (e) =>
          setError(apiErrorMessage(e, m.timeblocker_save_failed())),
        onSuccess: () => setQ(""),
      },
    );
  const hits = idle
    ? (recent.data?.items ?? [])
        .slice()
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, RECENT_CASES)
        .map(
          (c): SearchHit => ({
            id: c.id,
            kind: "case",
            label: c.title,
            matchCount: 0,
            snippets: c.openSteps
              .filter((s) => s.kind === "action" && s.status !== "dropped")
              .map((s) => ({
                field: "step",
                text: s.title,
                stepId: s.id,
                stepTitle: s.title,
                stepKind: "action",
                stepStatus: s.status,
              })),
          }),
        )
    : (data?.results.case ?? []).filter((hit) => hit.status !== "dropped");
  return (
    <div>
      <input
        aria-label={m.timeblocker_link_search()}
        placeholder={m.timeblocker_link_search()}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="plain-field w-full border-none bg-transparent py-2 text-[13px] outline-none"
      />
      <ul className="max-h-[260px] overflow-y-auto">
        {hits.map((hit) => (
          <CaseChoice
            key={`${q}:${hit.id}`}
            hit={hit}
            disabled={link.isPending}
            choose={(stepId) => choose(hit.id, stepId)}
          />
        ))}
      </ul>
      {q && !isFetching && data && !hits.length && (
        <p className="text-[12px] text-faint">{m.timeblocker_link_empty()}</p>
      )}
      {(error || searchError || recent.error) && (
        <p role="alert" className="text-[12px] text-danger">
          {error ??
            apiErrorMessage(
              searchError ?? recent.error,
              m.timeblocker_load_failed(),
            )}
        </p>
      )}
    </div>
  );
}

function CaseChoice({
  hit,
  disabled,
  choose,
}: {
  hit: SearchHit;
  disabled: boolean;
  choose(stepId?: string): void;
}) {
  const [expanded, setExpanded] = useState(false);
  const matched = [
    ...new Map(
      hit.snippets
        .filter(
          (s) =>
            s.field === "step" &&
            s.stepKind === "action" &&
            s.stepStatus !== "dropped" &&
            s.stepId,
        )
        .map((s) => [s.stepId, s]),
    ).values(),
  ];
  return (
    <li className="mb-3 text-[13px]">
      <div className="truncate py-1 text-[12px] text-muted">
        Case · {hit.label}
      </div>
      <button
        type="button"
        disabled={disabled}
        className="block w-full rounded px-2 py-1 text-left hover:bg-hover"
        onClick={() => choose()}
      >
        {m.timeblocker_case_add_whole()}
      </button>
      <button
        type="button"
        className="flex w-full items-center gap-1 rounded px-2 py-1 text-left text-muted hover:bg-hover"
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        <Caret open={expanded} />
        {m.timeblocker_pick_action()}
      </button>
      {expanded ? (
        <CaseActions caseId={hit.id} disabled={disabled} choose={choose} />
      ) : (
        matched.map((s) => (
          <ActionChoice
            key={s.stepId}
            title={String(s.stepTitle ?? s.text)}
            disabled={disabled}
            choose={() => choose(s.stepId)}
          />
        ))
      )}
    </li>
  );
}
function CaseActions({
  caseId,
  disabled,
  choose,
}: {
  caseId: string;
  disabled: boolean;
  choose(stepId?: string): void;
}) {
  const { data, error } = useQuery(caseQuery(useApi(), caseId));
  const actions =
    data?.steps.filter((s) => s.kind === "action" && s.status !== "dropped") ??
    [];
  return (
    <>
      {actions.map((s) => (
        <ActionChoice
          key={s.id}
          title={s.title}
          disabled={disabled}
          choose={() => choose(s.id)}
        />
      ))}
      {data && !actions.length && (
        <p className="pl-5 text-[12px] text-faint">
          {m.timeblocker_no_actions()}
        </p>
      )}
      {error && (
        <p role="alert" className="text-[12px] text-danger">
          {apiErrorMessage(error, m.timeblocker_save_failed())}
        </p>
      )}
    </>
  );
}
function ActionChoice({
  title,
  disabled,
  choose,
}: {
  title: string;
  disabled: boolean;
  choose(): void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      className="block w-full truncate rounded py-1 pr-2 pl-5 text-left hover:bg-hover"
      onClick={choose}
    >
      Action · {title}
    </button>
  );
}
